/**
 * Band boundaries for a diverging stacked rating.
 *
 * Lifted from Quick Study (`apps/frontend/src/components/quick-study-results/divergingStacked.ts`).
 * Quick Study is not migrated onto this module; a frontend parity test is the drift guard.
 *
 * R4.1 / crafted-exhibits Step 1: merge of this fork into the shared aggregation
 * lib is explicitly deferred.
 */

/**
 * The named statistic a band set measures. A declared rating
 * unit is banded by the canonical policy (`favorable_share`, `ratingPolicyBands`).
 * A scale that declares no unit gets the visual range split, its own statistic
 * (`visual_range_band`, catalogue row `calc.rating.visual_range_band`): it keeps
 * its declared range (`lowMax` / `highMin` inside `scaleMin`-`scaleMax`) and is
 * labelled by that range, never as "favorable".
 */
export type DivergingBandStatistic = 'visual_range_band' | 'favorable_share'

export interface DivergingBands {
  /** Which statistic these bands measure; see `DivergingBandStatistic`. */
  statistic: DivergingBandStatistic
  /** Odd scales have a neutral middle band; even scales split into halves. */
  hasNeutral: boolean
  /** Highest value counted in the LOW band (inclusive). */
  lowMax: number
  /** Lowest value counted in the HIGH band (inclusive). */
  highMin: number
  /** Lowest value on the scale (inclusive) — kept so labels can show value ranges. */
  scaleMin: number
  /** Highest value on the scale (inclusive) — kept so labels can show value ranges. */
  scaleMax: number
}

/** The visual range band: Bottom-2 | Neutral | Top-2 for odd scales (clamped so tiny
 *  scales never overlap: a 1–3 scale yields 1 | 2 | 3); bottom-half | top-half for even
 *  scales. Null for a degenerate scale. */
export function divergingBands(scaleMin: number, scaleMax: number): DivergingBands | null {
  const span = scaleMax - scaleMin + 1
  if (!Number.isFinite(span) || span < 2) return null
  if (span % 2 === 0) {
    const half = span / 2
    return {
      statistic: 'visual_range_band',
      hasNeutral: false,
      lowMax: scaleMin + half - 1,
      highMin: scaleMin + half,
      scaleMin,
      scaleMax,
    }
  }
  const mid = (scaleMin + scaleMax) / 2
  return {
    statistic: 'visual_range_band',
    hasNeutral: true,
    lowMax: Math.min(scaleMin + 1, mid - 1),
    highMin: Math.max(scaleMax - 1, mid + 1),
    scaleMin,
    scaleMax,
  }
}
